import type {FC} from 'react';
import React, {useState} from 'react';
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Button,
	HStack,
	Image,
	Stack,
	Text,
	VStack,
	useColorMode,
	useToast,
} from '@tempo/core';
import {useTranslation} from '@app/hooks/useTranslation';
import PermissionsList from './PermissionsList';
import {usePermission} from '@app/hooks/usePermission';
import type {Permission} from '@app/hooks/usePermission';
import {parseImageMd5toUrl} from '@app/utils/images';
import {config} from '@app/modules/config';
import {useModalController} from '@app/components/Modals/state';

const AppsPermissions: FC = () => {
	const t = useTranslation();
	const [accordionIndex, setAccordionIndex] = useState(-1);
	const {permissions, revokePermissions} = usePermission();
	const {colorMode} = useColorMode();
	const {openModal, closeModal} = useModalController();
	const toast = useToast();

	const LEGACY_PERMISSIONS = [
		{
			scope: 'b',
			label: t('applications.accessmybasicinfo'),
			description: t('applications.withname'),
		},
		{
			scope: 'e',
			label: t('applications.sendmemails'),
		},
		{
			scope: 'o',
			label: t('applications.accessinfoallthetime'),
		},
		{
			scope: 'mp',
			label: t('applications.title_managemymusic'),
			description: t('applications.text_managemymusic'),
		},
		{
			scope: 'mc',
			label: t('applications.title_managemycommunity'),
			description: t('applications.text_managemycommunity'),
		},
		{
			scope: 'del',
			label: t('applications.title_righttodelete'),
			description: t('applications.text_righttodelete'),
		},
		{
			scope: 'h',
			label: t('applications.accessmyhistory'),
		},
	];

	const isLegacyPermission = (permission: Permission) => {
		return typeof permission.permissions.scopes === 'string';
	};

	const toggleAccordion = (index: number) => {
		if (accordionIndex === index) {
			setAccordionIndex(-1);
		} else {
			setAccordionIndex(index);
		}
	};

	const handleOpen = (appId: string) => {
		openModal('action', {
			action: () => handleRevoke(appId),
			actionCTA: t('common.action_yes'),
			actionTestId: 'app-delete-confirm',
			closeCTA: t('common.action_no'),
			closeTestId: 'app-delete-cancel',
			message: [],
			title: t('applications.suredeletetheapp'),
		});
	};

	const handleRevoke = (clientId: string) => {
		revokePermissions(clientId).catch((error) => {
			toast({
				title: error.name,
				description: error.message,
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		});
		setAccordionIndex(-1);
		closeModal();
	};

	const getImageUrl = (permission: Permission) => {
		let md5 = permission.logo || '';
		if (permission.logoThemeDarkUrl && permission.logoThemeLightUrl) {
			md5 =
				colorMode === 'light'
					? permission.logoThemeLightUrl
					: permission.logoThemeDarkUrl;
		}
		return parseImageMd5toUrl(config.get('deezerImagesUrl'), md5);
	};

	if (!permissions) {
		return null;
	}

	const renderLegacyPermissions = (permission: Permission) => {
		if (permission.permissions.scopes === '*') {
			return (
				<>
					{LEGACY_PERMISSIONS.map((p) => (
						<Stack key={p.scope} spacing={2}>
							<Text fontWeight="bold" variant="body.s.default">
								{p.label}
							</Text>
							{p.description &&
								p.description
									.split('<br/>')
									.map((line: string, index: number) => (
										<Text
											key={index}
											color="text.neutral.primary.default"
											variant="body.s.default"
										>
											{line}
										</Text>
									))}
						</Stack>
					))}
				</>
			);
		} else {
			const scopes = (permission.permissions.scopes as string).split(',');
			return (
				<>
					{scopes.map((scope) => {
						const matchedPermission = LEGACY_PERMISSIONS.find(
							(p) => p.scope === scope,
						);
						if (!matchedPermission) return null;
						return (
							<Stack key={scope} spacing={2}>
								<Text fontWeight="bold" variant="body.s.default">
									{matchedPermission.label}
								</Text>
								{matchedPermission.description &&
									matchedPermission.description
										.split('<br/>')
										.map((line: string, index: number) => (
											<Text
												key={index}
												color="text.neutral.primary.default"
												variant="body.s.default"
											>
												{line}
											</Text>
										))}
							</Stack>
						);
					})}
				</>
			);
		}
	};

	return (
		<>
			<Accordion allowToggle index={accordionIndex} w="100%">
				{permissions.map((permission, index) => {
					return (
						<AccordionItem key={permission.appId}>
							<AccordionButton
								justifyContent={'space-between'}
								onClick={() => toggleAccordion(index)}
								data-testid={`app-${permission.appId}-expand`}
							>
								<HStack>
									<Image
										src={getImageUrl(permission)}
										alt={permission.appName}
										height="spacing.2xl"
										width="spacing.2xl"
										marginX="spacing.l"
									/>
									<VStack pl="spacing.l">
										<Text w="100%" textAlign="start">
											{permission.appName}
										</Text>
										<Text color="text.secondary" w="100%" textAlign="start">
											{permission.appDescription || t('common.details')}
										</Text>
									</VStack>
								</HStack>
								<AccordionIcon />
							</AccordionButton>
							<AccordionPanel paddingX="spacing.l">
								<Stack spacing="spacing.l">
									<Text fontWeight="bold">
										{t('applications.permissionsgiven')}
									</Text>
									{isLegacyPermission(permission) ? (
										renderLegacyPermissions(permission)
									) : (
										<PermissionsList
											scopes={permission.permissions.scopes as string[]}
											appId={permission.appId}
											spacing={0}
										/>
									)}
								</Stack>
								<Button
									onClick={() => handleOpen(permission.appId)}
									mt="spacing.l"
									data-testid={`app-${permission.appId}-delete`}
								>
									{t('applications.deletetheapp')}
								</Button>
							</AccordionPanel>
						</AccordionItem>
					);
				})}
			</Accordion>
			<style>{`
				html {
					overflow-y: scroll;
				}
			`}</style>
		</>
	);
};

export default AppsPermissions;
